// Unsaved work that survives a session ending. core.md §15.
//
// Every storage access is wrapped in try/catch and the app renders correctly
// with no stored value: private mode, a browser configured to block site data,
// and a quota error all throw from the accessor itself, and a throw there would
// take a screen down for a convenience.
//
//   composer drafts  — sessionStorage `chat_draft_<threadId | new:caseId>`
//                      (this TAB's typing; two tabs must not overwrite each other)
//   SOAP drafts      — localStorage `visit_draft_<encounterId>` (must survive a
//                      re-login, which is a full-document navigation)
//   UI preferences   — localStorage (`chat_sidebar_v1`, `visit_rail_v1`, …)

const storageOf = (kind) => {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

export function readDraft(key, { kind = 'session' } = {}) {
  try {
    const raw = storageOf(kind)?.getItem(key);
    return raw == null ? null : raw;
  } catch {
    return null;
  }
}

export function writeDraft(key, value, { kind = 'session' } = {}) {
  try {
    const store = storageOf(kind);
    if (!store) return false;
    if (value == null || value === '') store.removeItem(key);
    else store.setItem(key, String(value));
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(key, { kind = 'session' } = {}) {
  try { storageOf(kind)?.removeItem(key); } catch { /* storage off */ }
}

/** JSON variants, for structured drafts (the SOAP note, a UI preference). */
export function readJson(key, { kind = 'local', fallback = null } = {}) {
  const raw = readDraft(key, { kind });
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
}

export function writeJson(key, value, { kind = 'local' } = {}) {
  if (value == null) { clearDraft(key, { kind }); return true; }
  try { return writeDraft(key, JSON.stringify(value), { kind }); } catch { return false; }
}

/** The composer draft key: a thread, or a not-yet-saved conversation on a patient. */
export const chatDraftKey = ({ threadId, caseId }) =>
  `chat_draft_${threadId || `new:${caseId}`}`;

/** The SOAP draft key the Visit agent reads and writes. */
export const visitDraftKey = (encounterId) => `visit_draft_${encounterId}`;

export const CHAT_SIDEBAR_KEY = 'chat_sidebar_v1';
export const VISIT_RAIL_KEY = 'visit_rail_v1';
export const RECORD_SECTIONS_KEY = 'record_sections_v1';

/**
 * The onboarding form, while it is being typed. sessionStorage, like a composer
 * draft: the expired-session dialog's «ورود مجدد» is a FULL-DOCUMENT navigation
 * to Keycloak and back, and a `useState` form does not survive it (core.md §4.3,
 * "on re-login the form is re-shown with the typed values"). Cleared the moment
 * `completeStudentOnboarding` succeeds.
 */
export const ONBOARDING_DRAFT_KEY = 'onboarding_draft';
